import { IconButton } from '@creditornot/cb-button';
import { CaretDown, CaretUp, MagnifyingGlass } from '@creditornot/cb-icons';
import { cssThemedValue, Spinner } from '@creditornot/cb-ingredients';
import { Input } from '@creditornot/cb-input';
import { Radio } from '@creditornot/cb-radio';
import { useMutation, useQuery } from '@tanstack/react-query';
import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import {
  updatePrimaryLanguage,
  readMenu,
  deleteTranslations,
  readMenuAll,
} from 'apis/restaurant-api/merchant-admin/menu2/api';
import { Venue } from 'apis/restaurant-api/merchant-admin/venue/types';
import ErrorToastContent from 'common/components/ErrorToastContent';
import PageHeader from 'common/components/PageHeader';
import { Typography as T } from 'common/typography';
import { usePermissions } from 'common/wolt-permissions';
import * as permissionUtils from 'common/wolt-permissions/permissionUtils';
import { languages } from 'config';
import PageLoadError from 'core/PageLoadError';

import AddLanguage from './AddLanguage';
import LanguageComparisonSelector from './LanguageComparisonSelector';
import Sections from './Sections';
import TranslationStats from './TranslationStats';
import VerifyTranslationNotice from './VerifyTranslationNotice';
import useRequestQueue from '../hooks/useTranslationQueue';
import { LanguageStats, TranslationLabelItem, TranslationSection, TranslationSectionItem } from '../types';
import * as utils from '../utils';

type Props = {
  venue: Venue;
};

const MessageHeading = styled(T.Heading6)`
  margin-block-end: 12px;
`;

const TranslationStatsHeading = styled(T.Heading6)`
  margin-block-end: 12px;
  color: ${cssThemedValue('textPrimary')};
`;

const SectionsHeading = styled(T.Heading6)`
  margin-inline-end: 48px;
  color: ${cssThemedValue('textPrimary')};
`;

const StyledDisplayModeSelector = styled.div`
  display: flex;
  align-items: center;
`;

const DisplayModeLabel = styled.label`
  display: flex;
  align-items: center;
  & + & {
    margin-inline-start: 24px;
  }
`;

const DisplayModeSelector = ({
  filterUntranslated,
  setFilterUntranslated,
}: {
  filterUntranslated: boolean;
  setFilterUntranslated: (filterUntranslated: boolean) => void;
}) => {
  const intl = useIntl();
  return (
    <StyledDisplayModeSelector>
      <DisplayModeLabel>
        <Radio
          name="displayMode"
          checked={!filterUntranslated}
          onChange={() => {
            setFilterUntranslated(false);
          }}
        >
          {intl.formatMessage({ id: 'translation.showEverything' })}
        </Radio>
      </DisplayModeLabel>
      <DisplayModeLabel>
        <Radio
          name="displayMode"
          checked={filterUntranslated}
          onChange={() => {
            setFilterUntranslated(true);
          }}
        >
          {intl.formatMessage({ id: 'translation.showUntranslated' })}
        </Radio>
      </DisplayModeLabel>
    </StyledDisplayModeSelector>
  );
};

const Root = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: ${cssThemedValue('surfaceMain')};
`;

const SpinnerContainer = styled.div`
  display: flex;
  justify-content: center;
  padding-block-start: 40px;
  padding-block-end: 40px;
`;

const HeaderWrapper = styled.div`
  background: ${cssThemedValue('surfaceMain')};
  width: 100%;
`;

const LanguagesHeader = styled.div`
  display: flex;
  justify-content: space-between;
`;

const StyledVerifyTranslationNotice = styled(VerifyTranslationNotice)`
  margin-block-start: 24px;
`;

const Section = styled.div`
  width: 100%;
  margin: 0 auto;
  padding: 24px;
`;

const SectionWithDivider = styled(Section)`
  border-block-end: 1px solid ${cssThemedValue('borderPrimary')};
`;

const StyledTranslationSections = styled(Sections)`
  flex: 1;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
`;

const BodyHeader = styled.div`
  display: flex;
  justify-content: space-between;
`;

const BodyHeaderLeftContent = styled.div`
  display: flex;
`;

const SearchInput = styled(Input)`
  width: 240px;
`;

const searchInSections = (search: string, sections: TranslationSection[]) => {
  const normalizedSearch = search.toLocaleLowerCase();
  return sections.reduce<TranslationSection[]>((accSections, currSection) => {
    const filteredSubsections = currSection.subsections.filter(sub =>
      sub.items.some(item => item.translations.some(t => t.value.toLocaleLowerCase().includes(normalizedSearch))),
    );
    if (filteredSubsections.length === 0) {
      return accSections;
    }
    return [...accSections, { label: currSection.label, subsections: filteredSubsections }];
  }, []);
};

const Translation = ({ venue }: Props) => {
  const intl = useIntl();

  const {
    data: menuData,
    isLoading: isLoadingMenu,
    isError: fetchMenuError,
    refetch: refetchMenuData,
  } = useQuery(['read-menu-all', venue.menu], () => (venue.menu ? readMenuAll(venue.menu) : undefined), {
    refetchOnWindowFocus: false,
  });
  const sections = menuData && utils.buildSections(menuData.menu, menuData.items, menuData.options);
  const primaryLanguage = menuData?.menu.primary_language || null;

  const [languagesSectionOpen, setLanguagesSectionOpen] = useState(false);
  const [leftLanguage, setLeftLanguage] = useState<Language | null>(null);
  const [rightLanguage, setRightLanguage] = useState<Language | null>(null);
  // NOTE: Newly added languages are not removed from this list even when a translation for such language is added
  const [addedLanguages, setAddedLanguages] = useState<Language[]>([]);
  const [filterUntranslated, setFilterUntranslated] = useState(false);
  const [searchFilter, setSearchFilter] = useState('');

  const { addTranslationRequest, pendingTasks, erroredTasks } = useRequestQueue({
    menuData,
    rightLanguage,
    leftLanguage,
  });

  const addLanguage = (language: Language) => {
    setLeftLanguage(rightLanguage ? leftLanguage || language : leftLanguage || primaryLanguage);
    setRightLanguage(rightLanguage || language);
    setAddedLanguages([...addedLanguages, language]);
  };

  const permissions = usePermissions();
  const hasPermissionToChangeMenuLanguage = permissionUtils.hasMenuLanguageChangePermission(permissions);

  const { mutate: deleteSecondaryLanguage, isLoading: isDeletingSecondaryLanguage } = useMutation(
    async (language: Language) => {
      if (!venue.menu) {
        throw new Error('This venue does not yet have a menu!');
      }
      await deleteTranslations(venue.menu, language);
      setLeftLanguage(leftLanguage === language ? null : leftLanguage);
      setRightLanguage(rightLanguage === language ? null : rightLanguage);
      setAddedLanguages(addedLanguages.filter(l => l !== language));
    },
    {
      onSuccess: () => {
        refetchMenuData();
      },
      onError: error => {
        toast.error(<ErrorToastContent error={error} />, { duration: Infinity });
      },
    },
  );

  const { mutate: setPrimaryLanguage, isLoading: settingPrimaryLanguage } = useMutation(
    async (language: Language) => {
      if (!venue?.menu) {
        throw new Error('No venue or menu in state');
      }
      // TODO: use menuData.menu
      const menu = await readMenu(venue.menu);
      await updatePrimaryLanguage(menu.id, menu.version.number, language);
    },
    {
      onSuccess: () => {
        refetchMenuData();
      },
      onError: () => {
        toast.error(intl.formatMessage({ id: 'errors.unknown-error-retry-contact' }), { duration: Infinity });
      },
    },
  );

  let content;
  if (isLoadingMenu) {
    content = null;
  } else if (fetchMenuError) {
    content = <PageLoadError />;
  } else if (!venue || !venue.menu) {
    content = (
      <HeaderWrapper>
        <Section>
          <MessageHeading>
            <FormattedMessage id="translation.translationTool" />
          </MessageHeading>
          <T.Body1>
            <FormattedMessage id="translation.noMenuDescription" />
          </T.Body1>
        </Section>
      </HeaderWrapper>
    );
  } else if (!primaryLanguage) {
    content = (
      <HeaderWrapper>
        <Section>
          <MessageHeading>
            <FormattedMessage id="translation.translationTool" />
          </MessageHeading>
          <T.Body1>
            <FormattedMessage id="translation.primaryLanguageMissing" />
          </T.Body1>
        </Section>
      </HeaderWrapper>
    );
  } else if (sections && sections.length === 0) {
    content = (
      <HeaderWrapper>
        <Section>
          <MessageHeading>
            <FormattedMessage id="translation.translationTool" />
          </MessageHeading>
          <T.Body1>
            <FormattedMessage id="translation.noTranslatableMenuContent" />
          </T.Body1>
        </Section>
      </HeaderWrapper>
    );
  } else if (sections) {
    const statsByLanguage = new Map<Language, LanguageStats>();

    sections.forEach(section => {
      section.subsections.forEach(subsection => {
        subsection.items.forEach(item => {
          item.translations.forEach(t => {
            if (t.value) {
              const stats = {
                language: t.lang,
                translated: 0,
                unverified: 0,
                ...statsByLanguage.get(t.lang),
              };
              stats.translated += 1;
              if (!t.verified) {
                stats.unverified += 1;
              }
              statsByLanguage.set(t.lang, stats);
            }
          });
        });
      });
    });

    addedLanguages.forEach(language => {
      if (!statsByLanguage.get(language)) {
        statsByLanguage.set(language, {
          language,
          translated: 0,
          unverified: 0,
        });
      }
    });

    const availableLanguages = [...statsByLanguage.keys()];

    const visibleSections = filterUntranslated
      ? utils.filterUntranslated(
          [leftLanguage, rightLanguage].filter((l): l is Language => !!l && l !== primaryLanguage),
          sections,
        )
      : sections;

    const visibleSectionsWithSearch = searchFilter ? searchInSections(searchFilter, visibleSections) : visibleSections;

    const flatVisibleSections = visibleSectionsWithSearch.reduce<(TranslationLabelItem | TranslationSectionItem)[]>(
      (acc, curr) => {
        const subsection = curr.subsections.flatMap(subsection =>
          subsection.label
            ? [
                { itemType: 'subsection' as const, label: subsection.label, price: subsection.price },
                ...subsection.items,
              ]
            : subsection.items,
        );
        return [...acc, { itemType: 'section' as const, label: curr.label }, ...subsection];
      },
      [],
    );

    content = (
      <>
        <HeaderWrapper>
          <SectionWithDivider>
            <LanguagesHeader>
              <TranslationStatsHeading>
                <FormattedMessage id="translation.languages" />
              </TranslationStatsHeading>
              <IconButton variant="grey" size="small" onClick={() => setLanguagesSectionOpen(prev => !prev)}>
                {languagesSectionOpen ? <CaretUp /> : <CaretDown />}
              </IconButton>
            </LanguagesHeader>
            {languagesSectionOpen && (
              <>
                <TranslationStats
                  hasPermissionToChangeMenuLanguage={hasPermissionToChangeMenuLanguage}
                  stats={[...statsByLanguage.values()]}
                  loading={isDeletingSecondaryLanguage}
                  primaryLanguage={primaryLanguage}
                  pendingPrimaryLanguage={settingPrimaryLanguage}
                  onDeleteLanguage={deleteSecondaryLanguage}
                  onSetPrimaryLanguage={setPrimaryLanguage}
                  intl={intl}
                />
                <AddLanguage languages={languages.filter(l => !availableLanguages.includes(l))} onAdd={addLanguage} />
              </>
            )}
          </SectionWithDivider>
        </HeaderWrapper>
        <Section>
          <BodyHeader>
            <BodyHeaderLeftContent>
              <SectionsHeading>
                <FormattedMessage id="translation.translationTool" />
              </SectionsHeading>
              <DisplayModeSelector
                filterUntranslated={filterUntranslated}
                setFilterUntranslated={setFilterUntranslated}
              />
            </BodyHeaderLeftContent>
            <SearchInput
              stretch={false}
              onClearClick={() => setSearchFilter('')}
              placeholder="Search..."
              icon={<MagnifyingGlass />}
              disabled={!leftLanguage && !rightLanguage}
              value={searchFilter}
              onChange={e => setSearchFilter(e.target.value)}
            />
          </BodyHeader>
          <LanguageComparisonSelector
            availableLanguages={availableLanguages}
            leftLanguage={leftLanguage}
            rightLanguage={rightLanguage}
            onLeftLanguageChange={setLeftLanguage}
            onRightLanguageChange={setRightLanguage}
          />
          <StyledVerifyTranslationNotice
            primaryLanguage={primaryLanguage}
            statsByLanguage={statsByLanguage}
            leftLanguage={leftLanguage}
            rightLanguage={rightLanguage}
          />
        </Section>
        <StyledTranslationSections
          menuData={menuData}
          leftLanguage={leftLanguage}
          rightLanguage={rightLanguage}
          sections={flatVisibleSections}
          primaryLanguage={primaryLanguage}
          erroredTasks={erroredTasks}
          pendingTasks={pendingTasks}
          onSaveTranslation={addTranslationRequest}
        />
      </>
    );
  } else {
    return <PageLoadError />;
  }

  if (isLoadingMenu) {
    return (
      <SpinnerContainer>
        <Spinner size="large" />
      </SpinnerContainer>
    );
  }

  return (
    <Root>
      <PageHeader title={intl.formatMessage({ id: 'navigation.translation' })} />
      {content}
    </Root>
  );
};

export default Translation;
